'use client';
import { useTranslation } from '@root/src/i18n/client';
import { getCustomer, updateCustomer } from '@root/src/services/client/user';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getListOrderCustomer } from '@root/src/services/client/order';
import { convertCurrency } from '@root/src/lib/format';

const AccountPage = () => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState('personal');
  const [customer, setCustomer] = useState(null);
  const [listOrder, setListOrder] = useState([]);
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    phone: customer?.phone || '',
    email: customer?.email || '',
    address: customer?.address || '',
  });

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (customer?.id) {
      try {
        const updatedData = {
          name: formData.name || customer.name,
          phone: formData.phone || customer.phone,
          email: formData.email || customer.email,
          address: formData.address || customer.address,
        };

        await updateCustomer(updatedData);
        router.push('/result');
      } catch (error) {
        console.error('Unexpected error:', error);
        router.push('/error');
      }
    } else {
      console.error('Customer ID is missing');
    }
  };

  const { t } = useTranslation('common');

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const customerId = userData?.customers?.[0]?.id;

    const fetchData = async () => {
      try {
        const customerData = await getCustomer(customerId);
        setCustomer(customerData);

        if (customerData?.phone) {
          const ordersData = await getListOrderCustomer(customerData.phone);
          setListOrder(ordersData.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer?.name || '',
        phone: customer?.phone || '',
        email: customer?.email || '',
        address: customer?.address || '',
      });
    }
  }, [customer]);

  const statusMap = {
    0: t('Pending Approval'),
    1: t('Approved'),
    2: t('In Delivery'),
    3: t('Completed'),
    4: t('Pending Redelivery'),
    5: t('Cancelled'),
  };

  const tabs = [
    { id: 'personal', label: t('Personal Information') },
    { id: 'orders', label: t('Orders') },
    { id: 'changePassword', label: t('change_password') },
  ];

  const inputClassName =
    'mt-2 block w-full rounded-[16px] border border-[#ddd1be] bg-[#fffdfa] px-4 py-3 text-sm text-[#4d463b] outline-none transition placeholder:text-[#a39784] focus:border-[#b9924f] focus:ring-2 focus:ring-[#ead8b3]';

  return (
    <div className="mt-[50px] w-full bg-[linear-gradient(180deg,#f8f2e7_0%,#fcfaf5_48%,#f6eedf_100%)] px-4 py-8 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-6xl">
        <div className="overflow-hidden rounded-[32px] border border-[#e3d5bf] bg-white/80 shadow-[0_24px_60px_rgba(77,70,59,0.08)]">
          <div className="border-b border-[#eadfce] bg-[linear-gradient(135deg,rgba(247,241,230,0.95),rgba(255,255,255,0.92))] px-5 py-6 sm:px-8 sm:py-8">
            <p className="text-[12px] font-semibold uppercase tracking-[0.24em] text-[#a0813a]">
              Ly Tra Account
            </p>
            <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p
                  className="text-lg text-[#6e6758]"
                  style={{ fontFamily: 'Georgia, Times New Roman, serif' }}
                >
                  {t('Welcome')}, {customer?.name || 'Quý khách'}
                </p>
                <h1
                  className="mt-2 text-3xl leading-tight text-[#223A27] sm:text-4xl"
                  style={{ fontFamily: 'Georgia, Times New Roman, serif' }}
                >
                  {t('Your Account')}
                </h1>
              </div>

              <div className="rounded-[22px] border border-[#e6d7bf] bg-[#fbf6ee] px-4 py-3 text-sm text-[#6e6758]">
                Quản lý hồ sơ, theo dõi đơn hàng và cập nhật thông tin trong cùng một không gian đồng bộ với tone Ly Trà.
              </div>
            </div>
          </div>

          <div className="border-b border-[#eadfce] px-4 py-4 sm:px-8">
            <div className="flex flex-wrap gap-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition sm:px-5 ${
                    activeTab === tab.id
                      ? 'bg-[#667348] text-white shadow-[0_10px_24px_rgba(102,115,72,0.18)]'
                      : 'border border-[#e3d5bf] bg-[#fbf6ee] text-[#6e6758] hover:border-[#cdb27b] hover:text-[#223A27]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="px-4 py-6 sm:px-8 sm:py-8">
            {activeTab === 'personal' && (
              <form
                className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                onSubmit={handleSubmit}
              >
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#8d7f66]">
                    {t('Full Name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={inputClassName}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#8d7f66]">
                    {t('Phone Number')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClassName}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#8d7f66]">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClassName}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#8d7f66]">
                    {t('Address')}
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={inputClassName}
                  />
                </div>

                <div className="sm:col-span-2 flex justify-end pt-2">
                  <button
                    type="submit"
                    className="rounded-full border border-[#667348] bg-[#667348] px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white transition hover:bg-[#55603b]"
                  >
                    {t('Save')}
                  </button>
                </div>
              </form>
            )}

            {activeTab === 'orders' && (
              <div>
                <div className="mb-5 rounded-[24px] border border-[#e9dcc9] bg-[#fbf6ee] px-5 py-4">
                  <h2
                    className="text-2xl text-[#223A27]"
                    style={{ fontFamily: 'Georgia, Times New Roman, serif' }}
                  >
                    {t('Orders')}
                  </h2>
                  <p className="mt-1 text-sm text-[#6e6758]">
                    {t('List of your orders will be shown here.')}
                  </p>
                </div>

                {listOrder && Object.keys(listOrder).length > 0 ? (
                  <div className="overflow-hidden rounded-[24px] border border-[#e7dbc9] bg-white">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[720px] border-collapse">
                        <thead className="bg-[#f3ebdd] text-[#5a5348]">
                          <tr>
                            <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em]">{t('No.')}</th>
                            <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em]">{t('Order Code')}</th>
                            <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em]">{t('Created At')}</th>
                            <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em]">{t('Total Price')}</th>
                            <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em]">{t('Status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listOrder.map((order, index) => (
                            <tr key={order.id} className="border-t border-[#f0e5d4] text-[#4d463b]">
                              <td className="px-4 py-4">{index + 1}</td>
                              <td className="px-4 py-4 font-medium">{order.orderCode}</td>
                              <td className="px-4 py-4">{new Date(order.createdAt).toLocaleDateString()}</td>
                              <td className="px-4 py-4">{convertCurrency(order.total)}</td>
                              <td className="px-4 py-4">
                                <span className="rounded-full bg-[#f7f1e6] px-3 py-1 text-xs font-semibold text-[#667348]">
                                  {statusMap[order.statusId] || 'Không xác định'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="rounded-[20px] border border-dashed border-[#d9ccb8] bg-[#fffdfa] px-5 py-6 text-sm text-[#7b715f]">
                    {t('No orders found.')}
                  </p>
                )}
              </div>
            )}

            {activeTab === 'changePassword' && (
              <div className="max-w-2xl">
                <div className="mb-5 rounded-[24px] border border-[#e9dcc9] bg-[#fbf6ee] px-5 py-4">
                  <h2
                    className="text-2xl text-[#223A27]"
                    style={{ fontFamily: 'Georgia, Times New Roman, serif' }}
                  >
                    {t('Change Password')}
                  </h2>
                  <p className="mt-1 text-sm text-[#6e6758]">
                    Cập nhật mật khẩu với giao diện đồng bộ tone màu hiện tại của Ly Trà.
                  </p>
                </div>

                <form className="space-y-5">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#8d7f66]">
                      {t('Old Password')}
                    </label>
                    <input type="password" className={inputClassName} />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#8d7f66]">
                      {t('New Password')}
                    </label>
                    <input type="password" className={inputClassName} />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#8d7f66]">
                      {t('Confirm New Password')}
                    </label>
                    <input type="password" className={inputClassName} />
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      className="rounded-full border border-[#667348] bg-[#667348] px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white transition hover:bg-[#55603b]"
                    >
                      {t('Change Password')}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
